import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Clock, MapPin, Star } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { SelectField } from "@/components/SelectField";
import { MONTHS, MONTH_OPTIONS } from "@/lib/months";

const tabs = [
  { key: "theme", label: "Theme-wise" },
  { key: "group", label: "Group Packages" },
  { key: "trending", label: "Trending Packages" },
];

const TRENDING_TAGS = ["Bestseller", "Signature", "Trending", "Family Favourite", "Grand Circuit"];
const PAGE_SIZE = 6;

export const PopularPackages = ({ onEnquire }) => {
  const [active, setActive] = useState("theme");
  const [theme, setTheme] = useState(null);
  const [month, setMonth] = useState("all");
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/packages")
      .then((res) => setPackages(res.data))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, []);

  const themeOptions = useMemo(() => {
    const counts = {};
    packages
      .filter((p) => p.category !== "visa")
      .forEach((p) => (p.themes || []).forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([t]) => t).slice(0, 10);
  }, [packages]);

  useEffect(() => {
    if (!theme && themeOptions.length) setTheme(themeOptions[0]);
  }, [themeOptions, theme]);

  const filtered = useMemo(() => {
    const tours = packages.filter((p) => p.category !== "visa");
    const monthOk = (p) => month === "all" || (p.best_months || []).includes(Number(month));
    if (active === "trending") {
      return tours
        .filter((p) => monthOk(p) && (TRENDING_TAGS.includes(p.tag) || Number(p.rating) >= 4.8))
        .sort((a, b) => Number(b.rating) - Number(a.rating))
        .slice(0, PAGE_SIZE);
    }
    if (active === "group") {
      return tours.filter((p) => p.tour_type === "Group Tour" && monthOk(p)).slice(0, PAGE_SIZE);
    }
    return tours.filter((p) => (p.themes || []).includes(theme) && monthOk(p)).slice(0, PAGE_SIZE);
  }, [active, theme, month, packages]);

  const viewAllTo = active === "group"
    ? "/packages/domestic"
    : active === "theme"
      ? `/packages/${filtered[0]?.category || "domestic"}`
      : `/packages/${filtered[0]?.category || "domestic"}`;

  return (
    <section id="packages" data-testid="popular-packages" className="py-20 md:py-28 px-4 sm:px-8 lg:px-16 bg-surface/40">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12"
        >
          <div>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-4">01 — Curated For You</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-cream">
              Journeys travellers <span className="italic text-gold">keep coming back to.</span>
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="package-filter-tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`package-tab-${t.key}`}
                className={`text-xs font-mono tracking-widest uppercase px-5 py-2.5 rounded-full border transition-all duration-200 ${
                  active === t.key
                    ? "bg-gold text-ink border-gold"
                    : "border-cream/20 text-mist hover:border-gold/50 hover:text-gold"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="flex flex-wrap items-center gap-4 mb-10" data-testid="homepage-month-filter">
          <p className="text-xs font-mono tracking-widest uppercase text-faded flex items-center gap-2">
            <CalendarDays size={14} className="text-gold" /> Travelling in
          </p>
          <div className="w-48">
            <SelectField
              value={month}
              onChange={setMonth}
              testid="homepage-month-select"
              options={MONTH_OPTIONS}
            />
          </div>
          {month !== "all" && (
            <p className="text-xs text-mist">— showing journeys at their best in {MONTHS[Number(month) - 1]}</p>
          )}
        </div>

        {active === "theme" && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="homepage-theme-chips">
            {themeOptions.map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                data-testid={`homepage-theme-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`text-[11px] font-mono tracking-widest uppercase px-4 py-2 rounded-full border transition-all duration-200 ${
                  theme === t ? "bg-gold text-ink border-gold" : "border-cream/20 text-mist hover:border-gold/50 hover:text-gold"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="packages-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-[420px] rounded-2xl bg-card/60 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((p, i) => (
              <motion.article
                key={p.id}
                data-testid={`package-card-${p.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                className="group rounded-2xl overflow-hidden bg-card border border-gold/10 hover:border-gold/40 transition-colors duration-300 flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 bg-ink/80 backdrop-blur-sm text-gold text-[11px] font-mono tracking-widest uppercase px-3 py-1.5 rounded-full border border-gold/30">
                    {p.tag}
                  </span>
                  <span className="absolute top-4 right-4 flex items-center gap-1 bg-ink/80 backdrop-blur-sm text-cream text-xs px-3 py-1.5 rounded-full border border-cream/15">
                    <Star size={12} className="text-gold fill-gold" /> {p.rating}
                  </span>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-xl sm:text-2xl text-cream mb-2">
                    <Link to={`/package/${p.id}`} data-testid={`package-title-link-${p.id}`} className="hover:text-gold transition-colors">
                      {p.title}
                    </Link>
                  </h3>
                  <p className="flex items-center gap-1.5 text-xs text-mist mb-1.5">
                    <MapPin size={13} className="text-gold" /> {p.destination}
                  </p>
                  <p className="flex items-center gap-1.5 text-xs text-mist mb-4">
                    <Clock size={13} className="text-gold" /> {p.duration}
                  </p>

                  <ul className="space-y-1.5 mb-3">
                    {p.highlights.slice(0, 3).map((h) => (
                      <li key={h} className="text-xs text-faded flex items-start gap-2">
                        <span className="text-gold mt-0.5">◆</span> {h}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={`/package/${p.id}`}
                    data-testid={`package-view-details-${p.id}`}
                    className="text-xs text-gold/80 hover:text-gold inline-flex items-center gap-1 mb-4 transition-colors"
                  >
                    View full package <ArrowRight size={12} />
                  </Link>

                  <div className="mt-auto flex items-center justify-between pt-4 border-t border-cream/10">
                    <div>
                      <p className="text-[11px] uppercase tracking-widest text-faded">
                        {p.category === "visa" ? "Service fee" : "From"}
                      </p>
                      <p className="font-display text-xl text-gold" data-testid={`package-price-${p.id}`}>
                        {formatINR(p.price)}
                      </p>
                    </div>
                    <button
                      onClick={() => onEnquire(p.category)}
                      data-testid={`package-enquire-btn-${p.id}`}
                      className="group/btn flex items-center gap-1.5 text-xs font-semibold text-ink bg-gold px-5 py-2.5 rounded-full hover:bg-gold-hover transition-colors duration-200"
                    >
                      Enquire
                      <ArrowRight size={13} className="group-hover/btn:translate-x-0.5 transition-transform" />
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="mt-12 text-center">
            <Link
              to={viewAllTo}
              data-testid="view-all-packages-link"
              className="inline-flex items-center gap-2 text-sm text-gold border border-gold/40 px-8 py-3.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-200"
            >
              Explore the full collection <ArrowRight size={15} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
